import { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { LuCheck, LuChevronDown, LuEye, LuEyeOff, LuSearch } from 'react-icons/lu';
import { cx } from './cx';

export function Field({ label, hint, error, htmlFor, action, children, className }) {
  return (
    <div className={cx('field', className)}>
      {(label || action) && (
        <div className="field__row">
          {label && (
            <label className="field__label" htmlFor={htmlFor}>
              {label}
            </label>
          )}
          {action}
        </div>
      )}
      {children}
      {error ? (
        <p className="field__error" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="field__hint">{hint}</p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef(function Input({ size, className, invalid, ...rest }, ref) {
  return <input ref={ref} className={cx('input', size === 'lg' && 'input--lg', className)} aria-invalid={invalid || undefined} {...rest} />;
});

export const PasswordInput = forwardRef(function PasswordInput({ showLabel, hideLabel, ...rest }, ref) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="input-group">
      <Input ref={ref} type={visible ? 'text' : 'password'} style={{ paddingRight: '2.75rem' }} {...rest} />
      <span className="input-group__suffix">
        <button
          type="button"
          className="icon-btn icon-btn--sm"
          aria-label={visible ? hideLabel : showLabel}
          title={visible ? hideLabel : showLabel}
          onClick={() => setVisible((value) => !value)}
        >
          {visible ? <LuEyeOff aria-hidden="true" /> : <LuEye aria-hidden="true" />}
        </button>
      </span>
    </div>
  );
});

export const SearchInput = forwardRef(function SearchInput({ label, suffix, className, ...rest }, ref) {
  return (
    <div className={cx('input-group search-input', className)}>
      <LuSearch className="input-group__icon" aria-hidden="true" />
      <Input ref={ref} type="search" aria-label={label} {...rest} />
      {suffix && <span className="input-group__suffix">{suffix}</span>}
    </div>
  );
});

export function Select({ label, value, onChange, options, className, id }) {
  const fallbackId = useId();
  const buttonId = id ?? fallbackId;
  const listId = `${buttonId}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === value));
  const selected = options[selectedIndex];

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    listRef.current?.focus();
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const show = () => {
    setActive(selectedIndex);
    setOpen(true);
  };

  const close = (restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  };

  const choose = (index) => {
    const option = options[index];
    if (option && option.value !== value) onChange(option.value);
    close();
  };

  const onButtonKeyDown = (event) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      show();
    }
  };

  const onListKeyDown = (event) => {
    if (event.key === 'ArrowDown') setActive((index) => Math.min(options.length - 1, index + 1));
    else if (event.key === 'ArrowUp') setActive((index) => Math.max(0, index - 1));
    else if (event.key === 'Home') setActive(0);
    else if (event.key === 'End') setActive(options.length - 1);
    else if (event.key === 'Enter' || event.key === ' ') choose(active);
    else if (event.key === 'Escape') close();
    else if (event.key === 'Tab') {
      setOpen(false);
      return;
    } else if (event.key.length === 1) {
      const match = options.findIndex((option) => option.label.toLowerCase().startsWith(event.key.toLowerCase()));
      if (match >= 0) setActive(match);
      return;
    } else return;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div ref={rootRef} className={cx('select-wrap', open && 'is-open', className)}>
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        className="select"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={label ? `${label}: ${selected?.label ?? ''}` : undefined}
        onClick={() => (open ? close(false) : show())}
        onKeyDown={onButtonKeyDown}
      >
        <span className="select__value">{selected?.label}</span>
        <LuChevronDown className="select-wrap__icon" aria-hidden="true" />
      </button>
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-label={label}
          aria-activedescendant={`${listId}-${active}`}
          className="select-list"
          onKeyDown={onListKeyDown}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              id={`${listId}-${index}`}
              data-index={index}
              role="option"
              aria-selected={option.value === value}
              className={cx('select-list__option', index === active && 'is-active')}
              onMouseMove={() => index !== active && setActive(index)}
              onClick={() => choose(index)}
            >
              <span>{option.label}</span>
              {option.value === value && <LuCheck aria-hidden="true" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
